import { useState } from 'react'
import type { TestResult, TypeCode } from '@mindtype/shared'
import { TYPE_INTERPRETATIONS, TYPE_COLORS } from '@mindtype/shared'
import { ModeOverview } from './components/ModeOverview'
import { DimensionDetails } from './components/DimensionDetails'
import { CrossValidationResultCard } from './components/CrossValidationResult'
import { TypeDetailModal } from './components/TypeDetailModal'
import { TypeBrowser } from './components/TypeBrowser'
import { FeedbackForm } from './components/FeedbackForm'
import { InterpersonalModule } from './components/InterpersonalModule'
import { ShareCard } from './components/ShareCard'
import { SelfKnowledgeModule } from './components/SelfKnowledgeModule'
import { CareerDevelopmentModule } from './components/CareerDevelopmentModule'
import { setResultPublic } from './services/api'

interface ResultPageProps {
  result: TestResult
  onRestart: () => void
  saveStatus?: 'idle' | 'saving' | 'saved' | 'error'
  savedResult?: {
    id: string
    shareToken: string
    shareUrl: string
  } | null
}

// 标签配置
const TABS = [
  { id: 'result', name: '测试结果' },
  { id: 'self', name: '自我认知' },
  { id: 'career', name: '职业发展' },
  { id: 'interpersonal', name: '人际关系' },
] as const

type TabId = typeof TABS[number]['id']

export function ResultPage({ result, onRestart, saveStatus, savedResult }: ResultPageProps) {
  const [selectedTypeCode, setSelectedTypeCode] = useState<string | null>(null)
  const [isPublic, setIsPublic] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [copying, setCopying] = useState(false)
  const [showShareCard, setShowShareCard] = useState(false)
  const [activeTab, setActiveTab] = useState<TabId>('result')

  const userNaturalType = result.modes.natural as TypeCode
  const typeInfo = TYPE_INTERPRETATIONS[userNaturalType]
  const colorConfig = TYPE_COLORS[userNaturalType]

  const handleShare = async () => {
    if (!savedResult) return

    const response = await setResultPublic(savedResult.id, true)
    if (response.success) {
      setIsPublic(true)
      setShareUrl(savedResult.shareUrl)
    }
  }

  const handleCopyLink = async () => {
    if (!shareUrl) return

    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopying(true)
      setTimeout(() => setCopying(false), 2000)
    } catch (error) {
      console.error('Copy failed:', error)
    }
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return mins > 0 ? `${mins}分${secs}秒` : `${secs}秒`
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: `${colorConfig.primary}03` }}>
      <div className="max-w-3xl mx-auto">
        {/* ========== 页面头部 ========== */}
        <header className="text-center py-4 mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">认知风格分析报告</h1>
          <p className="text-gray-500 mt-2 text-sm">
            基于 {result.answers.length} 道题目的作答生成
            <span className="mx-2">·</span>
            用时 {formatDuration(result.duration)}
          </p>
        </header>

        {/* ========== 类型展示卡片 ========== */}
        <div
          className="rounded-2xl p-8 text-center mb-6"
          style={{ backgroundColor: `${colorConfig.primary}08`, border: `1px solid ${colorConfig.primary}15` }}
        >
          <div className="text-sm mb-3 tracking-wide" style={{ color: colorConfig.primary }}>你的认知类型</div>
          <div
            className="inline-block px-5 py-2.5 rounded-lg mb-4 shadow-sm"
            style={{ backgroundColor: colorConfig.primary }}
          >
            <span
              className="text-3xl font-bold tracking-widest font-mono"
              style={{ color: colorConfig.text }}
            >
              {userNaturalType}
            </span>
          </div>
          <h2
            className="text-2xl font-semibold mb-3"
            style={{ color: colorConfig.primary }}
          >
            {typeInfo?.name}
          </h2>
          <p className="text-gray-600 text-sm italic">"{typeInfo?.tagline}"</p>
        </div>

        {/* ========== 标签导航 ========== */}
        <nav className="flex justify-center mb-6">
          <div
            className="inline-flex rounded-lg p-1"
            style={{ backgroundColor: `${colorConfig.primary}10` }}
          >
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-5 py-2 rounded-md text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: activeTab === tab.id ? colorConfig.primary : 'transparent',
                  color: activeTab === tab.id ? colorConfig.text : colorConfig.primary,
                }}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </nav>

        {/* ========== 标签内容 ========== */}
        <div className="space-y-6">
          {/* 测试结果 */}
          {activeTab === 'result' && (
            <>
              {/* 三层模式总览 */}
              <ModeOverview modes={result.modes} onTypeClick={setSelectedTypeCode} colorConfig={colorConfig} />

              {/* 四维度详情 */}
              <DimensionDetails dimensions={result.dimensions} colorConfig={colorConfig} />

              {/* 交叉验证结果 */}
              <CrossValidationResultCard crossValidation={result.crossValidation} colorConfig={colorConfig} />
            </>
          )}

          {/* 自我认知 */}
          {activeTab === 'self' && (
            <SelfKnowledgeModule
              typeCode={userNaturalType}
              modes={result.modes}
              dimensions={result.dimensions}
              overallStability={result.overallStability}
              stableAxes={result.stableAxes}
              flexibleAxes={result.flexibleAxes}
              colorConfig={colorConfig}
            />
          )}

          {/* 职业发展 */}
          {activeTab === 'career' && (
            <CareerDevelopmentModule
              typeCode={userNaturalType}
              colorConfig={colorConfig}
            />
          )}

          {/* 人际关系 */}
          {activeTab === 'interpersonal' && (
            <InterpersonalModule
              userType={userNaturalType}
              colorConfig={colorConfig}
            />
          )}
        </div>

        {/* ========== 探索所有类型 ========== */}
        <div className="mt-6">
          <TypeBrowser userType={userNaturalType} colorConfig={colorConfig} />
        </div>

        {/* 类型详情模态框 */}
        <TypeDetailModal
          typeCode={selectedTypeCode || ''}
          isOpen={!!selectedTypeCode}
          onClose={() => setSelectedTypeCode(null)}
          isUserType={selectedTypeCode === userNaturalType}
        />

        {/* ========== 反馈收集 ========== */}
        {saveStatus === 'saved' && savedResult && (
          <div className="mt-6">
            <FeedbackForm resultId={savedResult.id} />
          </div>
        )}

        {/* ========== 邀请好友测试 ========== */}
        <div
          className="rounded-xl p-6 text-center mt-6"
          style={{ backgroundColor: `${colorConfig.primary}05`, border: `1px solid ${colorConfig.primary}15` }}
        >
          <h3 className="text-base font-semibold mb-2" style={{ color: colorConfig.primary }}>
            想知道你和朋友/同事的相处模式？
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            邀请TA也来测试，对照你们的类型组合，看看是天然契合、互补搭配，还是需要磨合。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setShowShareCard(true)}
              className="px-6 py-2.5 rounded-lg font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: colorConfig.primary, color: colorConfig.text }}
            >
              生成分享卡片
            </button>
            {saveStatus === 'saved' && !isPublic && (
              <button
                onClick={handleShare}
                className="px-6 py-2.5 rounded-lg font-medium transition-all hover:opacity-90"
                style={{
                  backgroundColor: 'white',
                  color: colorConfig.primary,
                  border: `1px solid ${colorConfig.primary}`,
                }}
              >
                生成分享链接
              </button>
            )}
            {shareUrl && (
              <button
                onClick={handleCopyLink}
                className="px-6 py-2.5 rounded-lg font-medium transition-all hover:opacity-90"
                style={{
                  backgroundColor: 'white',
                  color: colorConfig.primary,
                  border: `1px solid ${colorConfig.primary}`,
                }}
              >
                {copying ? '已复制' : '复制测试链接'}
              </button>
            )}
          </div>
        </div>

        {/* ========== 底部操作 ========== */}
        <footer className="text-center pt-8 pb-8">
          <button
            onClick={onRestart}
            className="px-8 py-3 font-medium rounded-lg transition-all hover:opacity-90 shadow-sm"
            style={{ backgroundColor: colorConfig.primary, color: colorConfig.text }}
          >
            重新测试
          </button>
          <p className="text-xs text-gray-400 mt-4">
            版本 {result.version} · {new Date(result.timestamp).toLocaleString('zh-CN')}
          </p>
        </footer>

        {/* 版权声明 */}
        <footer className="text-center text-xs text-gray-400 py-6" style={{ borderTop: `1px solid ${colorConfig.primary}10` }}>
          <p>© 2024 MindType</p>
          <p className="mt-1">内容受版权保护，未经授权禁止商业使用</p>
        </footer>
      </div>

      {/* 分享卡片模态框 */}
      {showShareCard && (
        <ShareCard result={result} onClose={() => setShowShareCard(false)} />
      )}
    </div>
  )
}
